// Shared field definitions for the Tracker (read-only mirror of the
// O3D Tracker Google Sheet — the sheet is the source of truth).

// The backend stores real dates as 'YYYY-MM-DD' and keeps everything else
// in date columns VERBATIM (status text like "Cancelled", freeform dates).
// Never reparse through new Date(): local-timezone parsing of freeform text
// (e.g. '5/6/2026') shifts/flips the displayed date vs the sheet.
export function fmtDate(v) {
  if (!v) return ''
  const s = String(v)
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : s
}
export function fmtValue(key, v, date) {
  if (v === null || v === undefined || v === '') return ''
  return date ? fmtDate(v) : String(v)
}

// Sheet noise that should read as "no data", muted (display only — the
// stored value stays verbatim per the sheet-is-master contract).
const PLACEHOLDERS = new Set(['na', 'n/a', '-', '--', 'not mentioned', 'tbd', 'nil', 'none'])
export function isPlaceholder(v) {
  return v != null && PLACEHOLDERS.has(String(v).trim().toLowerCase())
}

// numeric-ish sheet columns — right-aligned with tabular numerals
export const NUMERIC_COLS = new Set([
  'slNo', 'printDuration', 'effortHours', 'crEffortHours', 'volumeCc',
  'totalVolumeG', 'year', 'seriesNumber', 'sliceThickness',
  'spacingBetweenSlices', 'partCount',
])

// NO PATIENT FIELDS ANYWHERE IN THIS FILE (Deepak, 2026-08-11). The portal does
// not display patient data; the backend no longer sends or stores it. A case is
// identified by its Case ID. Doctor and hospital stay — they are the referring
// contacts, not patient identifiers.

// Case (header) fields, grouped for the detail panel. [key, label, isDate?]
export const CASE_GROUPS = [
  { title: 'Referral', fields: [
    ['hospitalName', 'Hospital'], ['doctorName', 'Doctor'],
  ] },
  { title: 'Clinical', fields: [
    ['studyDescription', 'Study'], ['deformityDescription', 'Deformity'], ['complexity', 'Complexity'],
    ['effortHours', 'Effort (h)'], ['crEffortHours', 'CR Effort (h)'], ['extractedBy', 'Extracted By'],
  ] },
  { title: 'Intake & Notes', fields: [
    ['source', 'Source'], ['comments', 'Comments'],
  ] },
]
// case fields that are system-managed (shown read-only)
export const CASE_META = [['caseNo', 'Case ID'], ['slNo', 'SL No.'], ['year', 'Year'], ['month', 'Month'], ['sourceTab', 'Sheet Tab']]

// DICOM acquisition tuple fields — rendered once per distinct acquisition
// (a case segmented multiple times has more than one). [key, label, isDate?]
export const ACQ_FIELDS = [
  ['seriesNumber', 'Series No.'], ['seriesDescription', 'Series Description'],
  ['sliceThickness', 'Slice Thickness'], ['spacingBetweenSlices', 'Slice Spacing'],
  ['contrast', 'Contrast'], ['thresholdRange', 'Threshold Range'],
  ['thresholdRange2', 'Threshold Range 2'], ['imagesGeneratedBy', 'Images / CR By'],
  ['imageCategory', 'Image Category'], ['dicomReceivedDate', 'DICOM Received', true],
]

// Part (line-item) fields — read-only detail. [key, label, isDate?]
export const PART_FIELDS = [
  ['partLabel', 'Part'], ['stlName', 'STL File'], ['kind', 'Kind'], ['anatomy', 'Anatomy'],
  ['printer', 'Printer'], ['status', 'Status'], ['printDuration', 'Print Dur.'],
  ['printCompletionDate', 'Printed', true], ['dispatchPlan', 'Dispatch Plan', true],
  ['dispatchActual', 'Dispatched', true], ['dimensionMm', 'Dimension (mm)'],
  ['volumeCc', 'Vol (cc)'], ['totalVolumeG', 'Total Vol (g)'], ['wipName', 'WIP Name'],
  ['invoice', 'Invoice'], ['cleanedOn', 'Cleaned On', true], ['cleanedBy', 'Cleaned By'],
  ['packedOn', 'Packed On', true], ['packedBy', 'Packed By'], ['labeledBy', 'Labeled By'],
  ['inspectedBy', 'Inspected By'], ['notes', 'Notes'],
]
// compact column set shown in the parts table by default
export const PART_TABLE_COLS = ['partLabel', 'stlName', 'kind', 'printer', 'status', 'dispatchActual', 'totalVolumeG']

// Case list columns
export const CASE_LIST_COLS = [
  { key: 'caseNo', label: 'Case ID', sortable: true },
  { key: 'doctorName', label: 'Doctor', sortable: true },
  { key: 'hospitalName', label: 'Hospital', sortable: true },
  { key: 'status', label: 'Status', sortable: false },
  { key: 'partCount', label: 'Parts', sortable: false },
  { key: 'year', label: 'Year', sortable: true },
]

// Sheet view — one row per sheet row, columns named EXACTLY like the
// spreadsheet the team has always used. [key, sheetLabel, isDate?, isCaseLevel?]
// isCaseLevel columns render only on the first row of a case group
// (mimics the sheet's merged cells).
export const SHEET_COLS = [
  ['slNo', 'SL No.', false, true],
  ['stlName', 'Delivered STL Name'],
  ['dicomReceivedDate', 'DICOM Recived Date', true],
  ['printer', 'Printer'],
  ['status', 'Status'],
  ['printDuration', 'Print Duration'],
  ['dispatchPlan', 'Dispatch-Plan', true],
  ['dispatchActual', 'Dispatch-Act', true],
  ['invoice', 'Invoice'],
  ['printCompletionDate', 'Print Completion Date', true],
  ['extractedBy', 'Extracted By', false, true],
  ['hospitalName', 'Hospital name', false, true],
  ['doctorName', 'Doctor Name', false, true],
  // the sheet's Patient Name / ID / YoB / Sex columns are deliberately absent
  ['studyDescription', 'Study Description', false, true],
  ['deformityDescription', 'Deformity Description', false, true],
  ['complexity', 'Complexity', false, true],
  ['effortHours', 'Effort (hours)', false, true],
  ['wipName', 'WIP Name'],
  ['dimensionMm', 'Dimension (mm)'],
  ['volumeCc', 'Volume per Part(CC)'],
  ['totalVolumeG', 'Total Volume (g)'],
  ['anatomy', 'Anatomy'],
  ['seriesNumber', 'Series Number'],
  ['seriesDescription', 'Series Description'],
  ['sliceThickness', 'Slice Thickness'],
  ['spacingBetweenSlices', 'Spacing between slices'],
  ['contrast', 'Contrast'],
  ['thresholdRange', 'Threshold Range'],
  ['thresholdRange2', 'Threshold Range 2'],
  ['imagesGeneratedBy', 'Images / CR generated by', false, true],
  ['imageCategory', 'Image / CR Category', false, true],
  ['crEffortHours', 'CR Effort (hours)', false, true],
  ['cleanedOn', 'Cleaned On', true],
  ['cleanedBy', 'Cleaned By'],
  ['packedOn', 'Packed On', true],
  ['packedBy', 'Packed By'],
  ['labeledBy', 'Labled By'],
  ['inspectedBy', 'Inspected By'],
  ['comments', 'Comments', false, true],
  ['source', 'Source', false, true],
  ['year', 'Year', false, true],
  ['month', 'Month', false, true],
]
// the ~15 columns the team works with daily (column picker default)
export const SHEET_COLS_DEFAULT = [
  'slNo', 'stlName', 'dicomReceivedDate', 'printer', 'status', 'printDuration',
  'dispatchPlan', 'dispatchActual', 'printCompletionDate', 'extractedBy',
  'hospitalName', 'doctorName', 'wipName', 'totalVolumeG',
]

// Reference-tab views: [key, label] column defs per endpoint.
export const REF_VIEWS = [
  { id: 'printer-log', label: 'Printer Log', cols: [
    ['slNo', 'SL'], ['printer', 'Printer'], ['status', 'Status'], ['caseNo', 'Case'],
    ['partName', 'Part'], ['anatomy', 'Anatomy'], ['durationHours', 'Hours'],
    ['success', 'Success'], ['failure', 'Failure'], ['remarks', 'Remarks'],
  ] },
  { id: 'print-info', label: 'Print Info', cols: [
    ['caseNo', 'Case'], ['doctorName', 'Doctor'],
    ['stlFileName', 'STL File'], ['volume', 'Volume'], ['fracktoryVersion', 'Fracktory v'],
    ['fracktoryGrams', 'Frk g'], ['gramsWithSupport', 'g +sup'], ['gramsWithoutSupport', 'g −sup'],
    ['fracktoryDuration', 'Frk dur'], ['actualDuration', 'Actual dur'], ['printer', 'Printer'],
  ] },
  { id: 'printer-service', label: 'Printer Service', cols: [
    ['slNo', 'SL'], ['date', 'Date'], ['printer', 'Printer'], ['typeOfWork', 'Work'],
    ['issues', 'Issues'], ['workDone', 'Work Done'], ['success', 'Success'],
    ['failure', 'Failure'], ['remarks', 'Remarks'],
  ] },
  { id: 'case-info', label: 'Case Info', cols: [
    ['slNo', 'SL'], ['doctorName', 'Doctor'], ['crImagesNeeded', 'CR/Images Needed'],
    ['crSize', 'CR Size'], ['maxillaMandibleGuide', 'Maxilla/Mandible Guides'],
    ['fibulaOsteotomyGuide', 'Fibula Osteotomy Guide'],
  ] },
  { id: 'doctors', label: 'Doctors', cols: [['name', 'Doctor Name']] },
]
// Matrix tabs rendered verbatim as grids.
export const GRID_VIEWS = [
  { id: 'Cases by Year', label: 'Cases by Year' },
  { id: 'Large Model Pricing', label: 'Pricing' },
]

// ── Board lanes (v6) ─────────────────────────────────────────────────
// Mirrors LANES in backend/src/services/trackerParse.js — same ids, same order.
// The backend also ships the list on `summary.lanes`, which is what the board
// renders from; this table supplies the colour + the empty-state copy, and is
// the fallback if an older payload arrives without `lanes`.
//
// Colours are a deliberate ramp along the pipeline, not one hue per lane:
// grey (not yet work) -> the two warm exception lanes (stopped) -> blue (in
// hand) -> indigo (on a machine) -> emerald (finished with). The warm band is
// the ONLY warm part of the ramp, so the eye lands on it first — which is the
// point of a standup board. Keep it to those two lanes.
//
// Orange vs amber carries the split: orange = waiting on someone outside
// Osteo3d (chase it), amber = we paused it ourselves (decide it). Adjacent hues
// because they are adjacent concepts; distinct enough to tell apart at the 6px
// swatch the lane header renders.
export const LANES = [
  { id: 'untriaged', label: 'Untriaged', accent: 'bg-gray-300', head: 'text-gray-500', empty: 'Nothing untriaged' },
  { id: 'incoming', label: 'Incoming', accent: 'bg-sky-400', head: 'text-sky-700', empty: 'No new cases' },
  { id: 'awaiting', label: 'Awaiting Inputs', accent: 'bg-orange-500', head: 'text-orange-700', empty: 'Nothing awaiting inputs' },
  { id: 'blocked', label: 'Blocked', accent: 'bg-amber-500', head: 'text-amber-700', empty: 'Nothing on hold 🎉' },
  { id: 'processing', label: 'Processing', accent: 'bg-blue-400', head: 'text-blue-700', empty: 'Nothing processing' },
  { id: 'design', label: 'Design & Planning', accent: 'bg-violet-400', head: 'text-violet-700', empty: 'Nothing in design' },
  { id: 'printing', label: 'Printing', accent: 'bg-indigo-500', head: 'text-indigo-700', empty: 'Nothing on a printer' },
  // The CR / images hard copy that ships with the model. Cyan keeps the ramp
  // reading left to right between indigo (on a machine) and teal (post-print).
  { id: 'imaging', label: 'Images / CR', accent: 'bg-cyan-500', head: 'text-cyan-700', empty: 'No CR / images in hand' },
  // Post-print: cleaning, packing, inspection. Teal sits between indigo (on a
  // machine) and emerald (finished with), keeping the ramp reading left to right.
  { id: 'finishing', label: 'Finishing', accent: 'bg-teal-500', head: 'text-teal-700', empty: 'Nothing in finishing' },
  { id: 'ready', label: 'Ready to dispatch', accent: 'bg-emerald-500', head: 'text-emerald-700', empty: 'Nothing waiting to go' },
  // Empty here no longer means "no drift" — a case with an unrecognised word
  // alongside a recognised one now files under its real lane and carries a `?`.
  // The old copy ("Every Status recognised") would flatly contradict the red
  // "Unknown status N" stat rendered directly above this column.
  { id: 'unmapped', label: 'Unmapped', accent: 'bg-rose-400', head: 'text-rose-700', empty: 'No case is unreadable — check ? marks for drift' },
]
export const LANE_BY_ID = Object.fromEntries(LANES.map((l) => [l.id, l]))

// Blocked reasons are the team's verbatim col-F words (AwaitingCroppingInfo,
// On Hold…). Spaced out for reading; never reworded beyond that.
export function prettyStatus(s) {
  const t = String(s || '').trim()
  if (!t) return ''
  return /^awaiting\S/i.test(t) ? t.replace(/^([Aa]waiting)(\S)/, '$1 $2').replace(/([a-z])([A-Z])/g, '$1 $2') : t
}

export function statusClasses(status) {
  const s = (status || '').toLowerCase()
  if (/bad data|invalid|error/.test(s)) return 'bg-red-100 text-red-700'
  if (/not started/.test(s)) return 'bg-amber-100 text-amber-700'   // team Case Status
  if (/in progress|active/.test(s)) return 'bg-blue-100 text-blue-700' // team Case Status
  if (/done|dispatch|deliver|complete/.test(s)) return 'bg-green-100 text-green-700'
  if (/cancel|reject/.test(s)) return 'bg-red-100 text-red-700'
  // Awaiting BEFORE hold, and orange not amber: the 2026-09-13 lane split gave
  // amber to Blocked and orange to Awaiting Inputs, and a shared amber here put
  // an amber stage pill beside an orange Awaiting chip on the same row.
  if (/await/.test(s)) return 'bg-orange-100 text-orange-700'
  if (/hold|blocked|pending/.test(s)) return 'bg-amber-100 text-amber-700'
  if (/production|print|wip|process/.test(s)) return 'bg-blue-100 text-blue-700'
  if (/plan|queue|schedul/.test(s)) return 'bg-indigo-100 text-indigo-700'
  if (/reference/.test(s)) return 'bg-gray-100 text-gray-500'
  return 'bg-gray-100 text-gray-600'
}

export function fmtAgo(ts) {
  if (!ts) return ''
  const ms = Date.now() - new Date(ts).getTime()
  if (!Number.isFinite(ms) || ms < 0) return ''
  const min = Math.floor(ms / 60000)
  if (min < 1) return 'just now'
  if (min < 60) return `${min} min ago`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h} h ago`
  return `${Math.floor(h / 24)} d ago`
}

// How far behind the sheet the board is, from the last recorded sync.
//
// The hourly Cloud Scheduler job is deliberately paused — sync is manual, by the
// ⟳ Sync now button — so the board is only ever as current as the last press.
// `Last synced 6 d ago` was rendered in 11px grey under the button, which reads
// as decoration: a board a week behind looked identical to a fresh one, and a
// case typed into the sheet that morning simply was not there. That is the
// defect this exists to fix, found 2026-09-13 on O3D-2026-09-01-0001.
//
// Pure, with `now` injected, so the thresholds are unit-testable.
// 12 h: long enough that a same-day sync never nags. 48 h: a working day has
// passed with nobody pressing the button.
export const SYNC_STALE_HOURS = 12
export const SYNC_VERY_STALE_HOURS = 48
// The timestamp comes from the server; the comparison happens on the viewer's
// clock. A browser a few minutes behind makes a sync that JUST succeeded look
// like it happened in the future. Treat that as skew, not bad data — otherwise
// the banner latches on immediately after a successful sync, which is the exact
// opposite of what it is for. Only a wildly future timestamp is 'unknown'.
export const SYNC_CLOCK_SKEW_TOLERANCE_MS = 5 * 60 * 1000

export function syncStaleness(at, now = Date.now()) {
  if (!at) return { level: 'never', hours: null }
  const ms = now - new Date(at).getTime()
  // An unparseable timestamp is bad data, not freshness — say so rather than
  // silently rendering the board as current.
  if (!Number.isFinite(ms)) return { level: 'unknown', hours: null }
  if (ms < 0) {
    return ms >= -SYNC_CLOCK_SKEW_TOLERANCE_MS
      ? { level: 'fresh', hours: 0 }
      : { level: 'unknown', hours: null }
  }
  const hours = ms / 3600000
  if (hours >= SYNC_VERY_STALE_HOURS) return { level: 'very-stale', hours }
  if (hours >= SYNC_STALE_HOURS) return { level: 'stale', hours }
  return { level: 'fresh', hours }
}

// Should the Tracker warn that the board is behind the sheet?
//
// Pure because the interesting part is not the thresholds but WHEN the answer is
// knowable. `summary` is null on first paint, which syncStaleness reads as
// 'never' — warn on that and a red "never been synced" banner flashes on every
// visit. A failed fetch is the same trap: all that is known is that the request
// did not land, not that nobody has ever synced.
export function shouldWarnStale({ loading, error, level }) {
  if (loading || error) return false
  return level != null && level !== 'fresh'
}

// The banner's tone and wording, as data. Extracted from the JSX because
// otherwise the only things NOT covered by a test were the parts a person
// actually reads — delete the banner from the markup and every test still
// passed. Returns null when there is nothing to say.
export function staleBannerCopy(level, agoText) {
  const behind = `Last synced ${agoText}. Cases added or edited in the sheet since then are not on this board.`
  switch (level) {
    case 'never':
      return { tone: 'red', message: 'The tracker has never been synced. The board stays empty until a sync runs.' }
    case 'very-stale':
      return { tone: 'red', message: behind }
    case 'stale':
      return { tone: 'amber', message: behind }
    // Bad data, not staleness — do not claim a duration that was never read.
    case 'unknown':
      return { tone: 'amber', message: 'The last sync time could not be read.' }
    default:
      return null
  }
}

// The blocker chip reflects WHAT the case is waiting on, not a generic label —
// the col-F word VERBATIM, only re-spaced, which is the same rule the board chip
// follows.
// This used to hand-roll `'Awaiting ' + k.slice(8)` off a lowercased key, which
// covered three values in a lookup table and mangled the other four:
// `AwaitingCroppingInfo` rendered "Awaiting croppinginfo", and the space-separated
// ones ("Awaiting Plate scan / additonal CT") rendered lowercased with a double
// space. Measured 2026-09-13. prettyStatus already splits these correctly.
//
// 🔴 IT MUST BE FED `openStates`, NOT `states`. deriveStandupStage lowercases its
// histogram keys (trackerParse.js:504), so prettyStatus would have no capitals
// left to split on and `awaitingnewdata` would render "awaiting newdata" — worse
// than the code this replaced. `openStates` is stageOf's verbatim histogram.
// Caught in review of this change; the fix had been asserted, not measured.
export function awaitingLabel(states = {}) {
  // SHORTEST, not first. stageOf picks the chip's word with `shortest(blocking)`,
  // so picking the first key in sheet-row order put two different labels on one
  // row: the pill read "AwaitingPayment" while this chip read "Awaiting Plate
  // scan / additonal CT" on the same case. Reproduced with the real parser in
  // review; matching stageOf's rule is what makes the comment above true.
  const keys = Object.keys(states).filter((k) => /await/i.test(k))
  if (!keys.length) return 'Awaiting'
  return prettyStatus(keys.reduce((a, b) => (a.length <= b.length ? a : b)))
}
